"use client"

import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { ArrowLeft, Calendar, Clock, User, Tag, ExternalLink, Share2 } from "lucide-react"
import "./NewsDetail.css"
import placeholder from "../../images/placeholder.png"

const NewsDetail = () => {
    const [news, setNews] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const { id } = useParams()
    const navigate = useNavigate()

    const API_BASE_URL = "https://imperia-backend.onrender.com/api/news"
    const BEARER_TOKEN = "YWRtaW5AZXhhbXBsZS5jb206c3VwZXJzZWN1cmU="

    const getAuthHeaders = () => ({
        Authorization: `Bearer ${BEARER_TOKEN}`,
        "Content-Type": "application/json",
    })

    const generateNewsData = (newsData) => ({
        id: newsData.newsId || newsData.id,
        headline: newsData.headline || newsData.title,
        description: newsData.description || newsData.content,
        fullContent: newsData.fullContent || newsData.content || newsData.description,
        date: newsData.publishedDate || newsData.date || newsData.createdAt,
        category: newsData.category,
        imageUrl: newsData.photo || newsData.image,
        author: newsData.author,
        url: newsData.url,
        source: newsData.source,
        tags: newsData.tags || [],
        readTime: newsData.readTime || calculateReadTime(newsData.fullContent || newsData.content || newsData.description),
    })

    const calculateReadTime = (content) => {
        if (!content) return "1 min read"
        const wordsPerMinute = 200
        const wordCount = content.split(" ").length
        const readTime = Math.ceil(wordCount / wordsPerMinute)
        return `${readTime} min read`
    }

    useEffect(() => {
        const fetchNewsDetail = async () => {
            if (!id) {
                setError("No news ID provided")
                setLoading(false)
                return
            }

            setLoading(true)
            setError(null)

            try {
                const response = await fetch(`${API_BASE_URL}/${id}`, {
                    method: "GET",
                    headers: getAuthHeaders(),
                })

                if (!response.ok) {
                    if (response.status === 404) {
                        throw new Error("News article not found")
                    }
                    throw new Error(`HTTP error! status: ${response.status}`)
                }

                const result = await response.json()

                if (result.success && result.data) {
                    const newsData = generateNewsData(result.data)
                    setNews(newsData)
                    console.log(newsData)
                } else {
                    throw new Error("Invalid API response format")
                }
            } catch (error) {
                console.error("Error fetching news detail:", error)
                setError(error.message)
            } finally {
                setLoading(false)
            }
        }

        fetchNewsDetail()
    }, [id])

    const formatDate = (dateString) => {
        if (!dateString) return "No date"
        const date = new Date(dateString)
        return date.toLocaleDateString("en-US", {
            year: "numeric",
            month: "long",
            day: "numeric",
        })
    }

    const formatTime = (dateString) => {
        if (!dateString) return "No time"
        const date = new Date(dateString)
        return date.toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
        })
    }

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: news.headline,
                    text: news.description,
                    url: window.location.href,
                })
            } catch (error) {
                console.log("Error sharing:", error)
            }
        } else {
            navigator.clipboard.writeText(window.location.href)
            alert("Link copied to clipboard!")
        }
    }

    const handleExternalLink = () => {
        if (news.url) {
            window.open(news.url, "_blank", "noopener,noreferrer")
        }
    }

    if (loading) {
        return (
            <div className="news-detail-page">
                <div className="loading-container">
                    <div className="loading-spinner"></div>
                    <p className="loading-text">Loading news article...</p>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="news-detail-page">
                <div className="error-container">
                    <h2>Error Loading Article</h2>
                    <p>{error}</p>
                    <div className="error-actions">
                        <button onClick={() => navigate("/news")} className="btn-secondary">
                            Back to News
                        </button>
                        <button onClick={() => window.location.reload()} className="btn-primary">
                            Try Again
                        </button>
                    </div>
                </div>
            </div>
        )
    }

    if (!news) {
        return (
            <div className="news-detail-page">
                <div className="error-container">
                    <h2>Article Not Found</h2>
                    <p>The requested news article could not be found.</p>
                    <button onClick={() => navigate("/news")} className="btn-primary">
                        Back to News
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="news-detail-page">
            {/* Header Navigation */}
            {/* <header className="news-detail-header">
        <div className="header-container">
          <button onClick={() => navigate("/news")} className="back-button">
            <ArrowLeft size={20} />
            <span>Back to News</span>
          </button>
          <div className="header-actions">
            <button onClick={handleShare} className="action-button">
              <Share2 size={18} />
              <span>Share</span>
            </button>
            {news.url && (
              <button onClick={handleExternalLink} className="action-button">
                <ExternalLink size={18} />
                <span>Original</span>
              </button>
            )}
          </div>
        </div>
      </header> */}

            {/* Main Content */}
            <main className="news-detail-main">
                {/* Article Header */}
                <div className="article-header">
                    {news.category && (
                        <div className="article-category">
                            <Tag size={16} />
                            <span>{news.category.charAt(0).toUpperCase() + news.category.slice(1)}</span>
                        </div>
                    )}
                    <h2 className="article-title">{news.headline}</h2>
                    {/* <p className="article-description">{news.description}</p> */}

                    {/* Article Meta */}
                    <div className="article-meta">
                        <div className="meta-left">
                            {news.author && (
                                <div className="meta-item">
                                    <User size={16} />
                                    <span>By {news.author}</span>
                                </div>
                            )}
                            <div className="meta-item">
                                <Calendar size={16} />
                                <span>{formatDate(news.date)}</span>
                            </div>
                            <div className="meta-item">
                                <Clock size={16} />
                                <span>{formatTime(news.date)}</span>
                            </div>
                        </div>
                        <div className="meta-right">
                            <span className="read-time">{news.readTime}</span>
                            {news.source && <span className="news-source">Source: {news.source}</span>}
                        </div>
                    </div>
                </div>

                {/* Article Image */}
                {news.imageUrl && (
                    <div className="article-image-container">
                        <img
                            src={news.imageUrl || placeholder}
                            alt={news.headline}
                            className="article-image"
                            onError={(e) => {
                                e.target.style.display = "none"
                            }}
                        />
                    </div>
                )}

                {/* Article Content */}
                <div className="article-content">
                    <div className="content-text">
                        {news.fullContent ? (
                            <div
                                dangerouslySetInnerHTML={{
                                    __html: news.fullContent.replace(/\n/g, "<br />"),
                                }}
                            />
                        ) : (
                            <p>{news.description}</p>
                        )}
                    </div>

                    {/* Tags */}
                    {news.tags && news.tags.length > 0 && (
                        <div className="article-tags">
                            <h4>Tags:</h4>
                            <div className="tags-list">
                                {news.tags.map((tag, index) => (
                                    <span key={index} className="tag">
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>

                {/* Article Footer */}
                <footer className="article-footer">
                    <div className="footer-actions">
                        <button onClick={handleShare} className="footer-button">
                            <Share2 size={18} />
                            <span>Share Article</span>
                        </button>
                        {news.url && (
                            <button onClick={handleExternalLink} className="footer-button">
                                <ExternalLink size={18} />
                                <span>Read Original</span>
                            </button>
                        )}
                    </div>
                    <div className="footer-meta">
                        <p>
                            Published on {formatDate(news.date)} at {formatTime(news.date)}
                        </p>
                        <p>News ID: {news.id}</p>
                        {news.source && <p>Source: {news.source}</p>}
                    </div>
                </footer>
            </main>
        </div>
    )
}

export default NewsDetail
